import { createApi } from '@reduxjs/toolkit/query/react';
import moment from 'moment';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import { convertStringsToOptionsObject } from '../utils/helpers';

export const dealsApi = createApi({
  tagTypes: ['DEAL', 'DEAL_LIST', 'DEAL_STATUS'],
  reducerPath: 'dealsApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    dealList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, forDashboard, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        if (forDashboard) {
          params.forDashboard = true;
          params.assignment = JSON.parse(localStorage.getItem('userId') || '');
        }
        return {
          url: `/wrd/api/deal/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data.content = (response?.data?.content?.[0] || []).map((item: any) => ({
          ...item,
          formattedLastModifiedDate: moment(item.lastModifiedDate).format('DD.MM.YYYY'),
          formattedCreateDate: moment(item.createDate).format('DD.MM.YYYY'),
          key: item.id,
        }));
        return response;
      },
      providesTags: () => ['DEAL_LIST'],
    }),
    dealById: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/deal?dealId=${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        const dealData = response.data || {};
        const client = dealData.client || {};

        return {
          ...dealData,
          clientDetails: {
            clientName: client.clientName,
            clientId: client.clientId,
            clientTypeName: client.clientType?.name,
            clientPriority: client.clientPriority,
            clientPriorityReason: client.reason,
          },
          contactPersons: (client.contactPersonList || []).map((person: any) => ({
            id: person.id,
            fullName: person.fullName,
            firstName: person.fullName?.split(' ')[0] || '',
            lastName: person.fullName?.split(' ').slice(1).join(' ') || '',
            department: person.department,
            role: person.role,
            language: {
              id: person.languageId,
              name: '', // Language name might need to be fetched from another source
            },
            emails: person.contacts?.EMAIL?.map((email: string) => ({ email })) || [],
            phones: person.contacts?.MOBILE?.map((phone: string) => ({ phone })) || [],
            contacts: person.contacts || {},
          })),
          isFinalStatus: ['REJECTED', 'CLOSED'].includes(dealData.status),
          formattedLastModifiedDate: dealData.lastModifiedDate
            ? moment(dealData.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss')
            : '',
          formattedCreatedDate: dealData.createDate ? moment(dealData.createDate).format('DD.MM.YYYY HH:mm:ss') : '',
        };
      },
      providesTags: () => ['DEAL'],
    }),
    dealStatusTypes: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/deal/deal-status-types`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = convertStringsToOptionsObject(response.data);
        return response.data;
      },
      providesTags: () => ['DEAL_STATUS'],
    }),
    dealManagement: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/deal/management/${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Ensure consistent data structure
        return {
          management: response.data || {},
        };
      },
      providesTags: () => ['DEAL'],
    }),
    updateDealManagement: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/deal/management',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['DEAL', 'DEAL_LIST'],
    }),
    rejectDeal: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/deal/reject',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['DEAL', 'DEAL_LIST'],
    }),
  }),
});

export const {
  useDealListQuery,
  useDealByIdQuery,
  useDealStatusTypesQuery,
  useDealManagementQuery,
  useUpdateDealManagementMutation,
  useRejectDealMutation,
} = dealsApi;
